<template>
  <div class="flex w-[548px] flex-col items-start gap-3 self-stretch pb-5">
    <section class="flex flex-col items-start gap-2 self-stretch">
      <UITextSmMedium>{{ t("common.iframeEmbed") }}</UITextSmMedium>
      <UIInput
        id="iFrameurl"
        :model-value="iframeUrl"
        type="textarea"
        :rows="7"
        readonly
      >
        <template #suffix
          ><Copy01Icon
            class="h-5 w-5 cursor-pointer"
            :class="activeClass"
            @click="copyLink" /></template
      ></UIInput>
    </section>
    <CookieConsentSettings
      :enabled="checkForCookieConsent"
      :provider="cookieConsentProvider"
      @update:enabled="onCookieConsentToggle"
      @update:provider="onProviderChange"
    />
  </div>
</template>

<script setup lang="ts">
import config from "@/config";
import { getFormEmbedUrlForRegion } from "@/config/regionalPreviewUrl";
import { useAppStore } from "@/store/app";
import {
  COOKIE_CONSENT_ENABLED_DEFAULT,
  COOKIE_CONSENT_PROVIDER_DEFAULT,
} from "@/util/constants";
import { buildBasicEmbedCode } from "@/util/embedCode";
import { openLinkInNewTab, sleep } from "@/util/methods";
import { Copy01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIInput, UITextSmMedium } from "@gohighlevel/ghl-ui";
import { defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import CookieConsentSettings from "./CookieConsentSettings.vue";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  isWhiteLabelled: {
    type: Boolean,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  widgetName: {
    type: String,
    required: true,
  },
  widgetId: {
    type: String,
    required: true,
  },
  baseUrl: {
    type: String,
    required: true,
  },
});

const iframeUrl = ref();
const activeClass = ref("text-black-600");
const checkForCookieConsent = ref(COOKIE_CONSENT_ENABLED_DEFAULT);
const cookieConsentProvider = ref<string>(COOKIE_CONSENT_PROVIDER_DEFAULT);

createEmbedUrl();

const copyLink = async () => {
  activeClass.value = "text-blue-600";
  store.copyToClipBoard(iframeUrl.value);
  await sleep(6000);
  activeClass.value = "text-black-600";
};

function createEmbedUrl() {
  buildIframeUrl();
}

function buildIframeUrl() {
  iframeUrl.value = buildBasicEmbedCode({
    baseUrl: props.baseUrl,
    fallbackScriptBaseUrl: getFormEmbedUrlForRegion(store.regionCode),
    isWhiteLabelled: props.isWhiteLabelled,
    environment: process.env.NODE_ENV,
    widgetName: props.widgetName,
    widgetId: props.widgetId,
    checkForCookieConsent: checkForCookieConsent.value,
    cookieConsentProvider: cookieConsentProvider.value,
  });
}

const onCookieConsentToggle = (enabled: boolean) => {
  checkForCookieConsent.value = enabled;
  buildIframeUrl();
};

const onProviderChange = (value: string) => {
  cookieConsentProvider.value = value;
  buildIframeUrl();
};

const redirectToFunnel = async (url) => {
  store.copyToClipBoard(iframeUrl.value);
  await sleep(1000);
  openLinkInNewTab(url);
};
</script>
